import { streamClient, ChannelType } from "../config/stream";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { ensureStreamConnection } from "./streamConnectionService";

export const ensureUserExists = async (userId) => {
  try {
    try {
      const { users } = await streamClient.queryUsers({ id: userId });
      if (users && users.length > 0) {
        return true; // User exists
      }
    } catch (error) {
      console.log(`Error querying user ${userId} in Stream:`, error.message);
    }

    const userDoc = await getDoc(doc(db, "tutors", userId));
    if (!userDoc.exists()) {
      const studentDoc = await getDoc(doc(db, "students", userId));
      if (!studentDoc.exists()) {
        throw new Error(`User ${userId} does not exist in Firebase`);
      }
      return createStreamUser(studentDoc.id, studentDoc.data());
    }
    return createStreamUser(userDoc.id, userDoc.data());
  } catch (error) {
    console.error(`Failed to ensure user ${userId} exists in Stream:`, error);
    throw error;
  }
};

const createStreamUser = async (userId, userData) => {
  try {
    // Create or update the user in Stream with basic information (no role modification)
    await streamClient.upsertUser({
      id: userId,
      name: userData.name || "User",
      image: userData.photoUrl || "",
      userType: userData.userType || "guest", // Keep the original userType for reference
    });

    return true;
  } catch (error) {
    console.error(`Failed to create user ${userId} in Stream:`, error);
    throw error;
  }
};

export const createStreamChannel = async ({
  id,
  type,
  members,
  name,
  image,
  description,
  created_by_id,
  member_roles,
}) => {
  try {
    // Ensure all members are unique
    const uniqueMembers = Array.from(new Set(members));
    // For group/class channels, ensure all members are added
    if (
      type === ChannelType.PREMIUM_GROUP ||
      type === ChannelType.STANDARD_GROUP ||
      type === ChannelType.PREMIUM_INDIVIDUAL_CLASS
    ) {
      // Log members being added
    }
    // Ensure all members exist in Stream before creating the channel
    await Promise.all(uniqueMembers.map((userId) => ensureUserExists(userId)));
    
    // Prepare channel data with custom fields for Firebase function identification
    const channelConfig = {
      name,
      members: uniqueMembers,
      image,
      description,
      member_roles,
      // Add custom data to help Firebase function identify the channel
      custom: {},
    };
    
    // For groups, add groupId to custom data (channel ID = group ID, but custom field helps Firebase function)
    if (type === ChannelType.STANDARD_GROUP || type === ChannelType.PREMIUM_GROUP) {
      channelConfig.custom.groupId = id;
      channelConfig.custom.channelType = type;
    }
    // For classes, add classId to custom data
    else if (type === ChannelType.PREMIUM_INDIVIDUAL_CLASS) {
      channelConfig.custom.classId = id;
      channelConfig.custom.channelType = type;
    }
    
    // Create the channel with all members
    const channel = streamClient.channel(type, id, channelConfig);
    // Watch the channel after creation
    await channel.watch();

    return channel;
  } catch (error) {
    console.error(`[StreamService] Error creating channel:`, error);
    throw error;
  }
};

export const addMemberToStreamChannel = async ({
  channelId,
  userId,
  role,
  type,
}) => {
  console.log(`[addMemberToStreamChannel] Called - channelId: ${channelId}, userId: ${userId}, type: ${type}, role: ${role}`);
  try {
    // Validate inputs - make sure we have valid channel types
    if (!Object.values(ChannelType).includes(type)) {
      console.error(`[addMemberToStreamChannel] Invalid channel type: ${type}`);
      throw new Error(`Invalid channel type: ${type}`);
    }

    // Check if Stream client is connected
    if (!streamClient || !streamClient.userID) {
      console.error(`[addMemberToStreamChannel] Stream client not connected!`);
      throw new Error("Stream client is not connected. Cannot add member to channel.");
    }
    console.log(`[addMemberToStreamChannel] Stream client connected: ${streamClient.userID}`);

    // Get Firestore data to determine if user should be admin/moderator
    let groupAdminId = null;
    try {
      if (
        type === ChannelType.STANDARD_GROUP ||
        type === ChannelType.PREMIUM_GROUP
      ) {
        const groupDoc = await getDoc(doc(db, "groups", channelId));
        if (groupDoc.exists()) {
          const groupData = groupDoc.data();
          groupAdminId = groupData.groupAdminId;
        }
      } else if (type === ChannelType.PREMIUM_INDIVIDUAL_CLASS) {
        const classDoc = await getDoc(doc(db, "classes", channelId));
        if (classDoc.exists()) {
          const classData = classDoc.data();
          groupAdminId = classData.adminId;
        }
      }
    } catch (firestoreErr) {
      console.warn(
        `Could not fetch Firestore data for channel ${channelId}, continuing anyway`
        );
      }

      // Determine the appropriate role (admin gets channel_moderator)
      const memberRole =
        groupAdminId === userId
          ? "channel_moderator"
          : role || "channel_member";

    // Use the helper function which handles all the complexity
    const { addUserToChannel } = await import("./streamChannelHelpers");
    const response = await addUserToChannel(channelId, userId, type, memberRole);

    // Force a query for ALL channel types to ensure it appears in user's channel list
    try {
      console.log(`[addMemberToStreamChannel] Force querying channel ${channelId} to refresh client cache`);
      await streamClient.queryChannels(
        {
          id: channelId,
          members: { $in: [userId] },
        },
        {},
        { watch: true, state: true }
      );
      console.log(`[addMemberToStreamChannel] Channel ${channelId} queried successfully`);
    } catch (queryError) {
      console.warn(`[addMemberToStreamChannel] Could not refresh channel cache: ${queryError.message}`);
    }

    return response;
  } catch (error) {
    console.error("Error adding member to stream channel:", error.message);
    if (error.response) {
      console.error("Error response:", error.response);
    }
    throw error;
  }
};

// Removed updateChannelWithFirestoreData - functionality moved to streamChannelHelpers

export const removeMemberFromStreamChannel = async ({
  channelId,
  userId,
  type,
}) => {
  try {
    const channel = streamClient.channel(type, channelId);
    await channel.watch();

    const response = await channel.removeMembers([userId]);

    return response;
  } catch (error) {
    console.error("Error removing member from stream channel:", error.message);
    if (error.response) {
      console.error("Error response:", error.response);
    }
    throw error;
  }
};

export const deleteStreamChannel = async ({ channelId, type }) => {
  try {
    const channel = streamClient.channel(type, channelId);
    await channel.watch();

    // Stop watching before deletion
    await channel.watch({ timeout: 10000 }); // Increased timeout to 10s

    const response = await channel.delete();

    return response;
  } catch (error) {
    console.error("Error deleting stream channel:", error.message);
    if (error.response) {
      console.error("Error response:", error.response);
    }
    throw error;
  }
};

/**
 * Updates a Stream channel with the latest data from Firestore
 * This is useful to ensure group names, images, and descriptions are in sync
 */
export const syncChannelWithFirestore = async ({ channelId, type }) => {
  return ensureStreamConnection(async () => {
    try {
      // Fetch data from Firestore based on channel type
      let firestoreData = null;

      if (type === "standard_group" || type === "premium_group") {
        const groupDoc = await getDoc(doc(db, "groups", channelId));
        if (groupDoc.exists()) {
          firestoreData = groupDoc.data();
        }
      } else if (type === "premium_individual_class") {
        const classDoc = await getDoc(doc(db, "classes", channelId));
        if (classDoc.exists()) {
          firestoreData = classDoc.data();
        }
      } else if (type === "one_to_one_chat") {
        // One-to-one chats don't have corresponding Firestore documents
        return {
          success: true,
          message: "No Firestore data for one-to-one chats",
        };
      }

      if (!firestoreData) {
        return { success: false, message: "No Firestore data found" };
      }

      // Get the Stream channel
      const channel = streamClient.channel(type, channelId);
      await channel.watch();

      // Prepare the update data
      const updateData = {};

      // Set name based on document type
      if (firestoreData.groupName) {
        updateData.name = firestoreData.groupName;
      } else if (firestoreData.className) {
        updateData.name = firestoreData.className;
      }

      // Set image if available
      if (firestoreData.imageUrl) {
        updateData.image = firestoreData.imageUrl;
      }

      // Set description if available
      if (firestoreData.groupDescription) {
        updateData.description = firestoreData.groupDescription;
      } else if (firestoreData.classDescription) {
        updateData.description = firestoreData.classDescription;
      }

      // Only update if we have data to update
      if (Object.keys(updateData).length > 0) {
        // For premium channels, we need special handling
        if (type === "premium_group" || type === "premium_individual_class") {
          try {
            // Get the admin ID for this channel
            const adminId = firestoreData.groupAdminId || null;

            channel.data.name = updateData.name;
            if (updateData.image) channel.data.image = updateData.image;
            if (updateData.description)
              channel.data.description = updateData.description;

            // If this user is the admin, they might be able to update properties on the server
            if (streamClient.userID === adminId) {
              try {
                await channel.update(updateData);
              } catch (adminUpdateError) {
                console.warn(
                  `Even admin couldn't update channel: ${adminUpdateError.message}`,
                );
                // Continue without throwing error
              }
            }
          } catch (permError) {
            console.warn(
              `Skipped channel server updates: ${permError.message}`,
            );
            // Continue anyway as this is not critical
          }
        } else {
          // For standard channels, we can update directly

          await channel.update(updateData);
        }
      }
    } catch (error) {
      console.error(
        `Error syncing channel ${channelId} (${type}) with Firestore:`,
        error.message,
      );
      return { success: false, message: error.message };
    }

    return { success: true, message: "Channel synced successfully" };
  });
};

export function getChannelDisplayName(channel, user) {
  if (channel.type === "one_to_one_chat") {
    const members = Object.values(channel.state?.members || {});
    const otherMember = members.find((member) => member.user?.id !== user.uid);
    return otherMember && otherMember.user ? otherMember.user.name : "Chat";
  }
  if (channel.data?.name && channel.data.name.trim() !== "") {
    return channel.data.name;
  }
  if (channel.type === "premium_group" || channel.type === "standard_group") {
    return "Group Chat";
  }
  if (channel.type === "premium_individual_class") {
    return "Class Chat";
  }
  return "Chat";
}

// Helper to add a user to a channel only if not already a member
export const addUserToChannelIfNeeded = async (channelId, userId, type) => {
  try {
    const channel = streamClient.channel(type, channelId);
    await channel.watch();
    const currentMembers = Object.keys(channel.state?.members || {});
    if (!currentMembers.includes(userId)) {
      await channel.addMembers([userId]);
      await channel.watch();
      // Verify user was added (members list updated)
    } else {
      console.log(
        `[StreamService] User ${userId} already a member of channel ${channelId}`,
      );
    }
  } catch (error) {
    console.error(`[StreamService] Error adding user to channel:`, error);
    throw error;
  }
};

// Removed joinChannelIfNeeded - functionality moved to streamChannelHelpers
